import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Base selector
const selectPaymentsState = (state: RootState) => state.payments;

// Core payment selectors (matching web implementation)
export const selectPayingForResidentId = createSelector(
  selectPaymentsState,
  (payments) => payments.payingForResidentId
);

export const selectPayingForLeaseId = createSelector(
  selectPaymentsState,
  (payments) => payments.payingForLeaseId
);

export const selectPayingWithSourceId = createSelector(
  selectPaymentsState,
  (payments) => payments.payingWithSourceId
);

export const selectAmountToPay = createSelector(
  selectPaymentsState,
  (payments) => payments.amountToPay
);

export const selectIsCustomAmount = createSelector(
  selectPaymentsState,
  (payments) => payments.isCustomAmount
);

export const selectIsPaymentModalOpen = createSelector(
  selectPaymentsState,
  (payments) => payments.isPaymentModalOpen
);

export const selectAssociatedLeaseAccount = createSelector(
  selectPaymentsState,
  (payments) => payments.associatedLeaseAccount
);

// Payment method selectors
export const selectSelectedPaymentMethod = createSelector(
  selectPaymentsState,
  (payments) => payments.selectedPaymentMethod
);

export const selectPaymentMethodExpired = createSelector(
  selectSelectedPaymentMethod,
  (paymentMethod) => {
    if (!paymentMethod) return false;
    
    const mm = Number(paymentMethod.expirationMonth || paymentMethod.expMonth);
    const yy = Number(paymentMethod.expirationYear || paymentMethod.expYear);
    
    if (!mm || !yy) return false;
    
    const expYear = yy < 100 ? 2000 + yy : yy;
    const exp = new Date(expYear, mm, 0, 23, 59, 59, 999);
    return Date.now() > exp.getTime();
  }
);

export const selectPaymentMethodBlocked = createSelector(
  selectSelectedPaymentMethod,
  (paymentMethod) => Boolean(paymentMethod?.blockedAt)
);

// Fee calculation selectors
export const selectCalculatedFees = createSelector(
  selectPaymentsState,
  (payments) => payments.calculatedFees
);

export const selectTotalPaymentAmount = createSelector(
  [selectAmountToPay, selectCalculatedFees],
  (amount, fees) => {
    if (!fees) return amount;
    return fees.total;
  }
);

// Wizard selectors
export const selectCurrentWizardStep = createSelector(
  selectPaymentsState,
  (payments) => payments.currentWizardStep
);

export const selectIsProcessingPayment = createSelector(
  selectPaymentsState,
  (payments) => payments.isProcessingPayment
);

export const selectPaymentError = createSelector(
  selectPaymentsState,
  (payments) => payments.paymentError
);

// Validation selectors
export const selectValidationErrors = createSelector(
  selectPaymentsState,
  (payments) => payments.validationErrors
);

export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

export const selectValidationError = (field: string) => createSelector(
  selectValidationErrors,
  (errors) => errors[field]
);

// Complex selectors
export const selectPaymentFormValid = createSelector(
  [
    selectAmountToPay,
    selectSelectedPaymentMethod,
    selectPaymentMethodExpired,
    selectPaymentMethodBlocked,
    selectHasValidationErrors
  ],
  (amount, paymentMethod, isExpired, isBlocked, hasErrors) => {
    return (
      amount > 0 &&
      paymentMethod &&
      !isExpired &&
      !isBlocked &&
      !hasErrors
    );
  }
);

export const selectCanProceedToConfirmation = createSelector(
  [selectPaymentFormValid, selectIsProcessingPayment],
  (isValid, isProcessing) => isValid && !isProcessing
);

// Combined payment data selector (similar to web's usePaymentData)
export const selectPaymentData = createSelector(
  [
    selectPaymentsState,
    selectPaymentMethodExpired,
    selectPaymentMethodBlocked,
    selectTotalPaymentAmount
  ],
  (payments, isExpired, isBlocked, totalAmount) => ({
    ...payments,
    isPaymentMethodExpired: isExpired,
    isPaymentMethodBlocked: isBlocked,
    totalPaymentAmount: totalAmount,
    canProceed: payments.selectedPaymentMethod && 
                !isExpired && 
                !isBlocked && 
                payments.amountToPay > 0,
  })
);

// Export all selectors as a group for easier imports
export const paymentsSelectors = {
  selectPaymentsState,
  selectPayingForResidentId,
  selectPayingForLeaseId,
  selectPayingWithSourceId,
  selectAmountToPay,
  selectIsCustomAmount,
  selectIsPaymentModalOpen,
  selectAssociatedLeaseAccount,
  selectSelectedPaymentMethod,
  selectPaymentMethodExpired,
  selectPaymentMethodBlocked,
  selectCalculatedFees,
  selectTotalPaymentAmount,
  selectCurrentWizardStep,
  selectIsProcessingPayment,
  selectPaymentError,
  selectValidationErrors,
  selectHasValidationErrors,
  selectValidationError,
  selectPaymentFormValid,
  selectCanProceedToConfirmation,
  selectPaymentData,
};
